// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;
  const contentType = headers?.['Content-Type'] || 'application/transit+json';

  let bodyMaybe = {};
  if (body != null) {
    if (contentType === 'application/transit+json' && typeof body !== 'string') {
      bodyMaybe = { body: serialize(body) };
    } else {
      bodyMaybe = { body };
    }
  }

  const fetchOptions = {
    credentials: credentials || 'include',
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(async res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const responseType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      if (responseType === 'application/json') {
        const data = await res.json();
        const e = new Error(data.error || data.message || 'Request failed');
        throw Object.assign(e, data);
      }
      const text = await res.text();
      throw new Error(text || `Request failed with status ${res.status}`);
    }
    if (responseType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (responseType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

/**
 * Sync the authenticated Sharetribe user to MongoDB.
 * Called after signup to keep user records consistent.
 */
export const syncMongoUser = () => {
  return post('/api/users/sync', {});
};

// ================ Product Import API ================ //

/**
 * Upload an Excel/CSV file for product import.
 * Uses window.fetch directly — the generic request() helper drops FormData bodies
 * because it only re-attaches bodies after transit serialization.
 * @param {FormData} formData - Must include `file`, `userId`, optionally `userEmail`, `userName`
 */
export const uploadProductImport = formData => {
  const url = `${apiBaseUrl()}/api/import/upload`;
  return window
    .fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // Do NOT set Content-Type — the browser must set it with the multipart boundary
    })
    .then(res => {
      if (res.status >= 400) {
        return res.json().then(data => {
          const e = new Error(data.error || 'Upload failed');
          throw e;
        });
      }
      return res.json();
    });
};

/**
 * Fetch import history for a user.
 * @param {string} userId
 * @param {number} page
 * @param {number} limit
 */
export const getImportHistory = (userId, page = 1, limit = 10, search = '') => {
  const params = new URLSearchParams({
    userId,
    page: String(page),
    limit: String(limit),
  });
  if (search) {
    params.set('search', search);
  }
  return request(`/api/import/history?${params.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Fetch detailed info for a single import record.
 * @param {string} importId
 * @param {string} userId
 */
export const getImportDetail = (importId, userId) => {
  return request(`/api/import/history/${importId}?userId=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Build the URL to download a success or failed CSV for an import.
 * @param {string} importId
 * @param {string} userId
 * @param {'success'|'failed'} type
 */
export const importDownloadUrl = (importId, userId, type) => {
  const base = apiBaseUrl();
  return `${base}/api/import/history/${importId}/download?userId=${encodeURIComponent(userId)}&type=${type}`;
};

/**
 * Build the URL for the Excel template download.
 * @param {string} [industry] - optional industry slug (clothing|electronics|furniture|food|chemicals)
 */
export const importTemplateUrl = (industry) => {
  const base = `${apiBaseUrl()}/api/import/template`;
  return industry ? `${base}?industry=${encodeURIComponent(industry)}` : base;
};

/**
 * List products (admin: all, user: own) with optional filters.
 * Pass scope: 'own' to always limit to this userId (factory My Products).
 */
export const getProducts = (
  userId,
  { status, page = 1, limit = 20, targetUserId, search, scope } = {}
) => {
  const params = new URLSearchParams({ userId, page, limit });
  if (status) params.set('status', status);
  if (targetUserId) params.set('targetUserId', targetUserId);
  if (search) params.set('search', search);
  if (scope) params.set('scope', scope);
  return request(`/api/products?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Get product status summary counts.
 * Pass scope: 'own' to always limit to this userId (factory My Products).
 */
export const getProductStatusSummary = (userId, targetUserId, { scope } = {}) => {
  const params = new URLSearchParams({ userId });
  if (targetUserId) params.set('targetUserId', targetUserId);
  if (scope) params.set('scope', scope);
  return request(`/api/products/summary?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Admin: review a single product.
 * @param {string} productId
 * @param {string} adminUserId
 * @param {'approve'|'reject'|'request_corrections'} action
 * @param {string} [notes]
 */
export const reviewProduct = (productId, adminUserId, action, notes = '') => {
  return request(`/api/products/${productId}/review`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ adminUserId, action, notes }),
  });
};

/**
 * Admin: bulk review multiple products.
 */
export const bulkReviewProducts = (productIds, adminUserId, action, notes = '') => {
  return request('/api/products/bulk-review', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ adminUserId, productIds, action, notes }),
  });
};

/**
 * Delete a product (owner or admin).
 */
export const deleteProduct = (productId, userId) => {
  return request(`/api/products/${productId}?userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Factory: manually create a single product.
 */
export const createProduct = (userId, fields) => {
  return request('/api/products/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, ...fields }),
  });
};

/**
 * Update a product (owner or admin). Status resets to pending_review.
 */
export const updateProduct = (productId, userId, fields) => {
  return request(`/api/products/${productId}/update`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, ...fields }),
  });
};

export const updateProductImageUrl = (productId, userId, imageUrl, imageId) => {
  return request(`/api/products/${productId}/image`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, imageUrl, imageId }),
  });
};

/**
 * Admin: list all users who have imported products.
 * @param {string} adminUserId
 * @param {string} search
 */
export const listImportUsers = (adminUserId, search = '') => {
  const params = new URLSearchParams({ adminUserId, search, limit: 20 });
  return request(`/api/users?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

// ── Agent Dashboard ──────────────────────────────────────────────────────────

export const getAgentDashboardSummary = userId => {
  const params = new URLSearchParams({ userId });
  return request(`/api/agent/dashboard?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getAgentFactories = (userId, { search = '', page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams({ userId, search, page, limit });
  return request(`/api/agent/factories?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getAgentProducts = (userId, { status, search = '', page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams({ userId, search, page, limit });
  if (status) params.set('status', status);
  return request(`/api/agent/products?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getAgentNotifications = userId => {
  const params = new URLSearchParams({ userId });
  return request(`/api/agent/notifications?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Factory assigns an agent to represent them. */
export const assignAgentByFactory = (factoryUserId, payload) => {
  return request('/api/factory/assign-agent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ factoryUserId, ...payload }),
  });
};

/** Factory: get current active agent assignment. */
export const getFactoryAssignedAgent = factoryUserId => {
  const params = new URLSearchParams({ factoryUserId });
  return request(`/api/factory/assigned-agent?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Factory removes their current agent (history preserved). */
export const unassignAgentByFactory = factoryUserId => {
  return request('/api/factory/unassign-agent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ factoryUserId }),
  });
};

/** Re-push active agent onto all published listings (fixes stale buyer UI). */
export const syncFactoryAgentToListings = factoryUserId => {
  return request('/api/factory/sync-agent-listings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ factoryUserId }),
  });
};

/** Buyer creates a quotation for the factory's sourcing agent (Mongo). */
export const createQuotation = body => {
  return request('/api/quotations/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

/** Agent: list quotations assigned to them. */
export const getAgentQuotations = (userId, { status, search = '', page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams({ userId, page, limit });
  if (status) params.set('status', status);
  if (search) params.set('search', search);
  return request(`/api/quotations/agent?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Buyer: list own quotations. */
export const getBuyerQuotations = (buyerUserId, { status, page = 1, limit = 50 } = {}) => {
  const params = new URLSearchParams({ buyerUserId, page, limit });
  if (status) params.set('status', status);
  return request(`/api/quotations/mine?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Get one quotation (buyer or agent). */
export const getQuotation = (id, userId) => {
  const params = new URLSearchParams({ userId });
  return request(`/api/quotations/${id}?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Buyer ↔ agent message on a quotation. */
export const sendQuotationMessage = (id, { userId, body }) => {
  return request(`/api/quotations/${id}/message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, body }),
  });
};

/** Accept / decline / counter / withdraw a quotation. */
export const respondToQuotation = (id, body) => {
  return request(`/api/quotations/${id}/respond`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

/** Buyer sends an enquiry to the sourcing agent (Mongo). */
export const createEnquiry = body => {
  return request('/api/enquiries/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

/** Agent: list buyer enquiries. */
export const getAgentEnquiries = (userId, { status, search = '', page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams({ userId, page, limit });
  if (status) params.set('status', status);
  if (search) params.set('search', search);
  return request(`/api/enquiries/agent?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Buyer: list own enquiries. */
export const getBuyerEnquiries = (buyerUserId, { page = 1, limit = 50 } = {}) => {
  const params = new URLSearchParams({ buyerUserId, page, limit });
  return request(`/api/enquiries/mine?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Get one enquiry (buyer or agent). */
export const getEnquiry = (id, userId) => {
  const params = new URLSearchParams({ userId });
  return request(`/api/enquiries/${id}?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Buyer ↔ agent message on an enquiry. */
export const sendEnquiryMessage = (id, { userId, body }) => {
  return request(`/api/enquiries/${id}/message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, body }),
  });
};

/** Close an enquiry. */
export const closeEnquiry = (id, userId) => {
  return request(`/api/enquiries/${id}/close`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
};

/** Buyer bookmarks a product listing (Mongo). */
export const saveProduct = body => {
  return request('/api/saved-products', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

/** Buyer removes a bookmarked product. */
export const unsaveProduct = (buyerUserId, listingId) => {
  return request('/api/saved-products', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ buyerUserId, listingId }),
  });
};

/** List buyer's saved products. */
export const getSavedProducts = (buyerUserId, { page = 1, limit = 50 } = {}) => {
  const params = new URLSearchParams({ buyerUserId, page, limit });
  return request(`/api/saved-products?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Check if a listing is saved by the buyer. */
export const checkSavedProduct = (buyerUserId, listingId) => {
  const params = new URLSearchParams({ buyerUserId, listingId });
  return request(`/api/saved-products/check?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Join (or update) an Order Aggregation pool for a listing. */
export const joinAggregation = body => {
  return request('/api/aggregation/join', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

/** List aggregation pools the buyer has joined. */
export const getMyAggregations = buyerUserId => {
  const params = new URLSearchParams({ buyerUserId });
  return request(`/api/aggregation/mine?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Current open pool for a listing (optional buyerUserId for "your share"). */
export const getAggregationByListing = (listingId, buyerUserId) => {
  const params = new URLSearchParams({ listingId });
  if (buyerUserId) params.set('buyerUserId', buyerUserId);
  return request(`/api/aggregation/by-listing?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Mark aggregation pool as quoted after buyer confirms quotation. */
export const confirmAggregationQuote = (poolId, buyerUserId) => {
  return request('/api/aggregation/confirm-quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ poolId, buyerUserId }),
  });
};

/** List in-app notifications for a buyer. */
export const getMyNotifications = userId => {
  const params = new URLSearchParams({ userId });
  return request(`/api/notifications/mine?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/** Mark notifications as read. */
export const markNotificationsRead = (userId, { notificationIds, all } = {}) => {
  return request('/api/notifications/mark-read', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, notificationIds, all }),
  });
};

/** Notify buyer after a transaction transition (production / delivery). */
export const notifyTransactionEvent = body => {
  return request('/api/notifications/tx-event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

const json = (path, method, body) =>
  request(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });

export const getCatalogue = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') q.set(k, String(v));
  });
  return request(`/api/catalogue/products?${q}`, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
};

export const createAutomaticQuote = body => json('/api/commercial/quotes/automatic', 'POST', body);

export const listCommercialAgents = (userId, search = '') => {
  const params = new URLSearchParams({ userId });
  if (search) params.set('search', search);
  return request(`/api/commercial/agents?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

export const listCommercialQuotes = (userId, role) =>
  request(`/api/commercial/quotes?userId=${encodeURIComponent(userId)}&role=${encodeURIComponent(role)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

export const getCommercialQuote = (id, userId, role = 'buyer') =>
  request(
    `/api/commercial/quotes/${id}?userId=${encodeURIComponent(userId)}&role=${encodeURIComponent(role)}`,
    { method: 'GET', headers: { 'Content-Type': 'application/json' } }
  );

export const transitionCommercialQuote = (id, body) =>
  json(`/api/commercial/quotes/${id}/transition`, 'POST', body);

export const reviseCommercialQuote = (id, body) =>
  json(`/api/commercial/quotes/${id}/revise`, 'POST', body);

export const createRfq = body => json('/api/commercial/rfq', 'POST', body);

export const listRfqs = (userId, role) =>
  request(`/api/commercial/rfq?userId=${encodeURIComponent(userId)}&role=${encodeURIComponent(role)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

export const assignRfqAgent = (id, body) => json(`/api/commercial/rfq/${id}/assign`, 'POST', body);

export const quoteFromRfq = (id, body) => json(`/api/commercial/rfq/${id}/quote`, 'POST', body);

export const listCommercialAudit = userId =>
  request(`/api/commercial/audit?userId=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
